import { Button, HStack, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';

interface SelectAllMatchingBannerLabels {
  /** Shown when the whole current page is selected but more rows match. */
  pageSelected: ReactNode;
  /** Action that extends the selection to the whole filtered set. */
  selectMatching: ReactNode;
  /** Shown when the whole filtered set is selected. */
  allMatchingSelected: ReactNode;
  /** Action that clears the selection. */
  clear: ReactNode;
}

interface SelectAllMatchingBannerProps {
  /** Whether every row on the current page is selected. */
  allPageSelected: boolean;
  /** Whether the whole filtered set is currently selected. */
  selectAllMatching: boolean;
  /** Number of rows on the current page. */
  pageCount: number;
  /** Total rows matching the current filter. */
  matchingTotal: number;
  onSelectAllMatching: () => void;
  onClear: () => void;
  labels: SelectAllMatchingBannerLabels;
  /** Chakra color palette for the highlight (defaults to blue). */
  colorPalette?: string;
}

/**
 * Generic "select all matching the filter" banner for paginated tables with bulk
 * actions. When the current page is fully selected and more rows match the
 * filter, it offers to extend the selection to the whole filtered set (and back).
 *
 * Stateless and i18n-agnostic: the consumer owns the selection state, wires the
 * callbacks, and supplies already-localized labels. Renders nothing when neither
 * state applies.
 */
const SelectAllMatchingBanner: React.FC<SelectAllMatchingBannerProps> = ({
  allPageSelected,
  selectAllMatching,
  pageCount,
  matchingTotal,
  onSelectAllMatching,
  onClear,
  labels,
  colorPalette = 'blue',
}) => {
  const hasMoreThanPage = matchingTotal > pageCount;

  if (!selectAllMatching && !(allPageSelected && hasMoreThanPage)) {
    return null;
  }

  return (
    <HStack
      w="full"
      justify="center"
      gap={2}
      px={4}
      py={2}
      colorPalette={colorPalette}
      bg="colorPalette.subtle"
      borderWidth="1px"
      borderColor="colorPalette.muted"
      rounded="md"
    >
      {selectAllMatching ? (
        <>
          <Text fontSize="sm" color="colorPalette.fg">
            {labels.allMatchingSelected}
          </Text>
          <Button variant="plain" size="xs" colorPalette={colorPalette} onClick={onClear}>
            {labels.clear}
          </Button>
        </>
      ) : (
        <>
          <Text fontSize="sm" color="colorPalette.fg">
            {labels.pageSelected}
          </Text>
          <Button
            variant="plain"
            size="xs"
            colorPalette={colorPalette}
            textDecoration="underline"
            onClick={onSelectAllMatching}
          >
            {labels.selectMatching}
          </Button>
        </>
      )}
    </HStack>
  );
};

export default SelectAllMatchingBanner;
